import { useEffect, useMemo, useState } from 'react';
import { Box, Button, IconButton, TextField, Typography } from '@mui/material';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import { Bullet, PlanCard } from './components';
import { ProficiencySummaryTiles } from './ProficiencySummaryTiles';
import {
  deriveAccommodationInputs,
  fetchSuggestedAccommodations,
  type AccommodationInputs,
} from './accommodations';
import { cachedAccommodations, cachedPlanSnapshot, cachedSuggestedItems } from './planCache';
import { COLORS, WARNING_CHIP } from './tokens';
import {
  type PlanStudent,
  areasForDevelopment,
  capabilities,
  hasWidaDescriptorLibrary,
  isLtel,
  ltelBannerYears,
} from './derive';
import {
  type PlanSnapshotContent,
  type PlanSnapshotInputs,
  appendPlanSnapshotScores,
  derivePlanSnapshotInputs,
  fetchPlanSnapshot,
} from './planSnapshotContent';
import { useReclassCriteria } from './reclassCriteriaContext';
import type { PlanCatalog, PlanSnapshotEdit, PlanSupportChange, SnapshotSaveInput } from './planRecordApi';

export type RawSnapshotLines = { capabilities: string[]; areasForDevelopment: string[] };
export type FrozenSnapshot = { capabilities: string[]; areasForDevelopment: string[]; supports: string[]; raw?: RawSnapshotLines };
export type SnapshotRecord = { snapshotEdits: PlanSnapshotEdit[]; supportsRemoved: PlanSupportChange[]; supportsAdded: PlanSupportChange[] };

function ColumnHeading({ children }: { children: string }) {
  return (
    <Typography
      sx={{ fontSize: 16, fontWeight: 700, color: COLORS.muted, textTransform: 'capitalize' }}
    >
      {children}
    </Typography>
  );
}

function LtelChip({ label }: { label: string }) {
  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.5,
        bgcolor: WARNING_CHIP.bg,
        color: WARNING_CHIP.fg,
        pl: 1,
        pr: 1.5,
        py: 0.5,
        borderRadius: 999,
      }}
    >
      <WarningAmberRoundedIcon sx={{ fontSize: 24 }} />
      <Typography sx={{ fontSize: 12, fontWeight: 700, lineHeight: '16px' }}>{label}</Typography>
    </Box>
  );
}

function Marker({ verb, name }: { verb: string; name: string }) {
  return (
    <Typography component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, fontSize: 12, fontWeight: 600, lineHeight: '16px', color: COLORS.muted, whiteSpace: 'nowrap', pt: 0.5 }}>
      <EditOutlinedIcon sx={{ fontSize: 16 }} />
      {`${verb} by ${name}`}
    </Typography>
  );
}

function Line({ text, marker }: { text: string; marker?: { verb: string; name: string } }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
      <Bullet>{text}</Bullet>
      {marker && <Marker verb={marker.verb} name={marker.name} />}
    </Box>
  );
}

type AccommodationsState =
  | { status: 'loading' }
  | { status: 'ready'; items: string[] }
  | { status: 'unavailable' }
  | { status: 'error' };

type SupportDraft = { key: string | null; text: string };
type Draft = { capabilities: string[]; areas: string[]; supports: SupportDraft[] };

const EMPTY_RECORD: SnapshotRecord = { snapshotEdits: [], supportsRemoved: [], supportsAdded: [] };

export function PlanSnapshot({
  student,
  studentId,
  year,
  onAccommodations,
  onPlanSnapshot,
  frozen = null,
  record = null,
  canEdit = false,
  onSaveEdits,
  onEditingChange,
  catalog = null,
  alternateAccess = false,
  onSuggestedItems,
  onSettled,
  pastPlan = false,
}: {
  student: PlanStudent | null;
  studentId: string;
  year: number | null;
  onAccommodations?: (items: string[], inputs: AccommodationInputs) => void;
  onPlanSnapshot?: (content: PlanSnapshotContent, inputs: PlanSnapshotInputs) => void;
  frozen?: FrozenSnapshot | null;
  record?: SnapshotRecord | null;
  canEdit?: boolean;
  onSaveEdits?: (input: SnapshotSaveInput) => Promise<void>;
  onEditingChange?: (editing: boolean) => void;
  catalog?: PlanCatalog | null;
  alternateAccess?: boolean;
  onSuggestedItems?: (ids: string[]) => void;
  onSettled?: (generator: 'planSnapshot' | 'accommodations') => void;
  pastPlan?: boolean;
}) {
  const criteria = useReclassCriteria();
  const capabilityItems = student ? capabilities(student, criteria) : [];
  const developmentItems = student ? areasForDevelopment(student, criteria) : [];

  const snapshotInputs = useMemo(
    () => (frozen || !student || !hasWidaDescriptorLibrary(criteria) ? null : derivePlanSnapshotInputs(student, criteria)),
    [frozen, student, criteria],
  );
  const [generated, setGenerated] = useState<{
    inputs: PlanSnapshotInputs;
    content: PlanSnapshotContent;
    raw: RawSnapshotLines;
  } | null>(null);
  useEffect(() => {
    if (!snapshotInputs || !student) {
      onSettled?.('planSnapshot');
      return;
    }
    let cancelled = false;
    cachedPlanSnapshot(studentId, snapshotInputs)
      .then((saved) => saved ?? fetchPlanSnapshot(studentId, snapshotInputs))
      .then((content) => {
        if (cancelled) return;
        if (content.capabilities.length === 4) {
          setGenerated({
            inputs: snapshotInputs,
            content: appendPlanSnapshotScores(content, student, criteria),
            raw: { capabilities: content.capabilities, areasForDevelopment: content.areasForDevelopment },
          });
          onPlanSnapshot?.(content, snapshotInputs);
        }
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) onSettled?.('planSnapshot');
      });
    return () => {
      cancelled = true;
    };
  }, [snapshotInputs, studentId, student, criteria, onPlanSnapshot, onSettled]);

  const activeGenerated =
    generated && generated.inputs === snapshotInputs ? generated.content : null;
  const rawLines: RawSnapshotLines | null = frozen ? frozen.raw ?? null : generated && generated.inputs === snapshotInputs ? generated.raw : null;
  const rawLine = (list: keyof RawSnapshotLines, i: number, shown: string) => rawLines?.[list][i] ?? shown;
  const ltelYears = student ? ltelBannerYears(student) : null;
  const ltelName = student?.firstName || 'This student';
  const ltelMessage =
    student && isLtel(student)
      ? pastPlan
        ? ltelYears != null
          ? `${ltelName} was in Year ${ltelYears} of EL classification and was classified as LTEL.`
          : `${ltelName}'s years of EL classification aren't in your file.`
        : ltelYears != null
          ? `${ltelName} is in Year ${ltelYears} of EL classification — currently classified as LTEL.`
          : null
      : null;

  const inputs = useMemo(() => (frozen || !student ? null : deriveAccommodationInputs(student)), [frozen, student]);
  const [accommodations, setAccommodations] = useState<AccommodationsState>(
    inputs ? { status: 'loading' } : { status: 'unavailable' },
  );
  useEffect(() => {
    if (!inputs) {
      onSettled?.('accommodations');
      return;
    }
    let cancelled = false;
    cachedAccommodations(studentId, inputs)
      .then(async (saved) => {
        if (saved) {
          const ids = await cachedSuggestedItems(studentId, inputs);
          return { accommodations: saved, items: ids ?? [] };
        }
        return fetchSuggestedAccommodations(studentId, inputs, catalog, alternateAccess);
      })
      .then((result) => {
        if (cancelled) return;
        if (result.accommodations.length > 0) {
          setAccommodations({ status: 'ready', items: result.accommodations });
          onAccommodations?.(result.accommodations, inputs);
          onSuggestedItems?.(result.items);
        } else {
          setAccommodations({ status: 'error' });
        }
      })
      .catch(() => {
        if (!cancelled) setAccommodations({ status: 'error' });
      })
      .finally(() => {
        if (!cancelled) onSettled?.('accommodations');
      });
    return () => {
      cancelled = true;
    };
  }, [inputs, studentId, onAccommodations, onSuggestedItems, catalog, alternateAccess, onSettled]);

  const supportsReady = frozen !== null || accommodations.status === 'ready';
  const generatedSupports: string[] = frozen ? frozen.supports : accommodations.status === 'ready' ? accommodations.items : [];
  const generatedLines: FrozenSnapshot | null = frozen
    ? frozen
    : activeGenerated
      ? { capabilities: activeGenerated.capabilities, areasForDevelopment: activeGenerated.areasForDevelopment, supports: generatedSupports }
      : null;
  const overlay = record ?? EMPTY_RECORD;
  const editFor = (key: string, generated: string) => overlay.snapshotEdits.find((e) => e.key === key && e.generatedText === generated);
  const removed = new Set(overlay.supportsRemoved.map((s) => s.text));

  const shownCapabilities = generatedLines ? generatedLines.capabilities : capabilityItems;
  const shownDevelopment = generatedLines ? generatedLines.areasForDevelopment : developmentItems;
  const supportsShown: { text: string; marker?: { verb: string; name: string } }[] = [
    ...generatedSupports
      .map((text, i) => ({ text, edit: editFor(`supports.${i}`, text) }))
      .filter(({ text }) => !removed.has(text))
      .map(({ text, edit }) => (edit ? { text: edit.text, marker: { verb: 'Edited', name: edit.by.name } } : { text })),
    ...overlay.supportsAdded.map((s) => ({ text: s.text, marker: { verb: 'Added', name: s.by.name } })),
  ];

  const [draft, setDraft] = useState<Draft | null>(null);
  const [saving, setSaving] = useState(false);
  const editable = canEdit && generatedLines !== null && supportsReady && onSaveEdits != null;
  const startEditing = () => {
    if (!generatedLines) return;
    setDraft({
      capabilities: generatedLines.capabilities.map((text, i) => editFor(`capabilities.${i}`, rawLine('capabilities', i, text))?.text ?? text),
      areas: generatedLines.areasForDevelopment.map((text, i) => editFor(`areas.${i}`, rawLine('areasForDevelopment', i, text))?.text ?? text),
      supports: [
        ...generatedLines.supports
          .map((text, i) => ({ key: `supports.${i}`, text: editFor(`supports.${i}`, text)?.text ?? text, generated: text }))
          .filter(({ generated }) => !removed.has(generated))
          .map(({ key, text }) => ({ key, text })),
        ...overlay.supportsAdded.map((s) => ({ key: null, text: s.text })),
      ],
    });
    onEditingChange?.(true);
  };
  const stopEditing = () => {
    setDraft(null);
    onEditingChange?.(false);
  };
  const save = async () => {
    if (!draft || !generatedLines || !onSaveEdits || saving) return;
    const edits: { key: string; text: string }[] = [];
    draft.capabilities.forEach((text, i) => {
      const trimmed = text.trim();
      if (trimmed && trimmed !== generatedLines.capabilities[i]) edits.push({ key: `capabilities.${i}`, text: trimmed });
    });
    draft.areas.forEach((text, i) => {
      const trimmed = text.trim();
      if (trimmed && trimmed !== generatedLines.areasForDevelopment[i]) edits.push({ key: `areas.${i}`, text: trimmed });
    });
    const keptKeys = new Set(draft.supports.map((s) => s.key));
    const supportsRemoved = generatedLines.supports.filter((text, i) => removed.has(text) || !keptKeys.has(`supports.${i}`));
    const supportsAdded: string[] = [];
    draft.supports.forEach((s) => {
      const trimmed = s.text.trim();
      if (!trimmed) return;
      if (s.key === null) supportsAdded.push(trimmed);
      else {
        const i = Number(s.key.split('.')[1]);
        if (trimmed !== generatedLines.supports[i]) edits.push({ key: s.key, text: trimmed });
      }
    });
    setSaving(true);
    try {
      await onSaveEdits({ edits, supportsRemoved, supportsAdded });
      stopEditing();
    } catch {
      return;
    } finally {
      setSaving(false);
    }
  };

  const field = (value: string, onChange: (next: string) => void, extra?: React.ReactNode) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <TextField
        multiline
        fullWidth
        size="small"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        sx={{ '& .MuiOutlinedInput-root': { fontSize: 16, lineHeight: 1.5, color: COLORS.ink, borderRadius: 2 } }}
      />
      {extra}
    </Box>
  );

  const title = (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
      <Typography sx={{ fontSize: 24, fontWeight: 600, color: COLORS.ink, lineHeight: '32px' }}>Plan Snapshot</Typography>
      {draft ? (
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button onClick={stopEditing} sx={{ textTransform: 'none', fontWeight: 600, fontSize: 16, color: COLORS.ink }}>Cancel</Button>
          <Button variant="contained" disabled={saving} onClick={() => { void save(); }} sx={{ textTransform: 'none', fontWeight: 600, fontSize: 16, bgcolor: COLORS.navy, '&:hover': { bgcolor: '#23236F' } }}>Save changes</Button>
        </Box>
      ) : editable ? (
        <Button startIcon={<EditOutlinedIcon />} onClick={startEditing} sx={{ textTransform: 'none', fontWeight: 600, fontSize: 16, color: COLORS.navy }}>Edit</Button>
      ) : null}
    </Box>
  );

  return (
    <PlanCard sx={{ gap: 2, ...(draft ? { boxShadow: `0 0 0 2px ${COLORS.navy}` } : {}) }}>
      {title}
      {ltelMessage && <LtelChip label={ltelMessage} />}

      <Box sx={{ display: 'flex', gap: 2.5, alignItems: 'stretch' }}>
        <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: draft ? 1 : 0.5 }}>
          <ColumnHeading>Capabilities</ColumnHeading>
          {draft
            ? draft.capabilities.map((text, i) => (
                <Box key={`capabilities.${i}`}>{field(text, (next) => setDraft({ ...draft, capabilities: draft.capabilities.map((t, j) => (j === i ? next : t)) }))}</Box>
              ))
            : shownCapabilities.map((s, i) => {
                const edit = generatedLines ? editFor(`capabilities.${i}`, rawLine('capabilities', i, s)) : undefined;
                return <Line key={`${i}-${s}`} text={edit?.text ?? s} marker={edit ? { verb: 'Edited', name: edit.by.name } : undefined} />;
              })}
        </Box>
        <Box sx={{ alignSelf: 'stretch', width: '1px', bgcolor: COLORS.divider, flexShrink: 0 }} />
        <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: draft ? 1 : 0.5 }}>
          <ColumnHeading>Areas for Development</ColumnHeading>
          {draft ? (
            draft.areas.map((text, i) => (
              <Box key={`areas.${i}`}>{field(text, (next) => setDraft({ ...draft, areas: draft.areas.map((t, j) => (j === i ? next : t)) }))}</Box>
            ))
          ) : shownDevelopment.length > 0 ? (
            shownDevelopment.map((s, i) => {
              const edit = generatedLines ? editFor(`areas.${i}`, rawLine('areasForDevelopment', i, s)) : undefined;
              return <Line key={`${i}-${s}`} text={edit?.text ?? s} marker={edit ? { verb: 'Edited', name: edit.by.name } : undefined} />;
            })
          ) : (
            <Typography sx={{ fontSize: 13, fontStyle: 'italic', color: COLORS.muted }}>
              No priority development areas surfaced from the current assessment data.
            </Typography>
          )}
        </Box>
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: draft ? 1 : 0.5 }}>
        <ColumnHeading>Suggested Supports</ColumnHeading>
        {draft ? (
          <>
            {draft.supports.map((s, i) => (
              <Box key={`${s.key ?? 'added'}-${i}`}>
                {field(
                  s.text,
                  (next) => setDraft({ ...draft, supports: draft.supports.map((t, j) => (j === i ? { ...t, text: next } : t)) }),
                  <IconButton size="small" aria-label="Remove this support" onClick={() => setDraft({ ...draft, supports: draft.supports.filter((_, j) => j !== i) })}>
                    <CloseIcon fontSize="small" />
                  </IconButton>,
                )}
              </Box>
            ))}
            <Button startIcon={<AddIcon />} onClick={() => setDraft({ ...draft, supports: [...draft.supports, { key: null, text: '' }] })} sx={{ alignSelf: 'flex-start', textTransform: 'none', fontWeight: 600, fontSize: 16, color: COLORS.navy }}>
              Add support
            </Button>
          </>
        ) : (
          <>
            {supportsReady &&
              supportsShown.map((s, i) => <Line key={`${i}-${s.text}`} text={s.text} marker={s.marker} />)}
            {!frozen && accommodations.status === 'loading' && (
              <Typography sx={{ fontSize: 13, fontStyle: 'italic', color: COLORS.muted }}>
                Generating suggested supports…
              </Typography>
            )}
            {!frozen && accommodations.status === 'unavailable' && (
              <Typography sx={{ fontSize: 13, fontStyle: 'italic', color: COLORS.muted }}>
                Not enough scored history to generate supports yet.
              </Typography>
            )}
            {!frozen && accommodations.status === 'error' && (
              <Typography sx={{ fontSize: 13, fontStyle: 'italic', color: COLORS.muted }}>
                Couldn&rsquo;t generate supports right now.
              </Typography>
            )}
          </>
        )}
      </Box>

      {student ? <ProficiencySummaryTiles student={student} year={year} /> : null}

      <Typography sx={{ fontSize: 16, fontWeight: 500, lineHeight: '24px', color: COLORS.muted }}>
        {`Capability and growth statements are adapted from WIDA Can Do Descriptors matched to the student's current ACCESS results. Suggested Accommodations are generated from overall proficiency level as a starting point for teacher judgment.`}
      </Typography>
    </PlanCard>
  );
}
